import React, {useEffect, useMemo, useState} from "react";
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";

import {TextField} from "@components/Form/TextField";
import {SelectField, SelectOption} from "@components/Form/SelectField";
import {TextareaField} from "@components/Form/TextareaField";

import {useDropzone} from "react-dropzone";

import "./ContactForm.scss";
import {ChevronRightIcon} from "@components/Icons/ChevronRightIcon";
import {useProjectTranslations} from "@i18n/i18nUtils";
import {TFunction} from "i18next";
import {Issue, Ticket} from "@lib/Contact/ContactModels";
import {useContactIssues} from "@lib/Contact/ContactHooks";
import {useModalStore} from "@store/ModalStore";
import {useShallow} from "zustand/react/shallow";
import ContactService from "@lib/Contact/ContactService";
import {useRedemptions} from "@lib/Redemption/RedemptionHooks";
import {formatDate} from "@commons/Utils";
import ContentIoService from "@lib/ContentIo/ContentIoService";
import {
    CONTENTIO_ACCESS_MODIFIER,
    CONTENTIO_MODULE_ENUM,
    CONTENTIO_SUBMODULE_ENUM
} from "@lib/ContentIo/ContentIoModels";

export const makeContactSchema = (t: TFunction, issues: Issue[]) =>
    z
        .object({
            name: z.string().min(1, t("contacts:form:validation:name_required")),
            surname: z.string().min(1, t("contacts:form:validation:surname_required")),
            email: z
                .email(t("contacts:form:validation:email_invalid"))
                .min(1, t("contacts:form:validation:email_required")),

            reason: z.string().min(1, t("contacts:form:validation:reason_required")),
            specificReason: z
                .string()
                .min(1, t("contacts:form:validation:secondary_reason_required")),

            orderNumber: z.string().optional(),

            message: z.string().min(1, t("contacts:form:validation:message_required")),

            attachment: z.instanceof(File).optional().nullable(),
        })
        .superRefine((data, ctx) => {
            if (!issues || issues.length === 0) return;

            const parent = issues.find((p) => p.externalId === data.reason);
            if (!parent) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["reason"],
                    message: t("contacts:form:validation:reason_invalid"),
                });
                return;
            }

            const child = (parent.children ?? []).find((c) => c.externalId === data.specificReason);
            if (!child) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["specificReason"],
                    message: t("contacts:form:validation:secondary_reason_invalid"),
                });
                return;
            }

            const orderRequired = !!child.rewardIssue;
            if (orderRequired && (!data.orderNumber || data.orderNumber.trim().length === 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["orderNumber"],
                    message: t("contacts:form:validation:order_nr_required"),
                });
            }
        });

export function ContactForm() {

    const {t, i18n} = useProjectTranslations();

    const {openModal} = useModalStore(useShallow(s => ({
        openModal: s.open
    })));

    const {data: issues, isLoading: isLoadingIssues, isError: isErrorIssues} = useContactIssues();

    const {
        data: redemptionsData,
        isLoading: isLoadingRedemptions,
        isError: isErrorRedemptions,
    } = useRedemptions();

    const contactSchema = useMemo(
        () => makeContactSchema(t, issues ?? []),
        [t, i18n.language, issues]
    );

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        trigger,
        clearErrors,
        reset,
        formState: {errors, isSubmitting, isValid},
    } = useForm<z.infer<typeof contactSchema>>({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            name: "",
            surname: "",
            email: "",
            reason: "",
            specificReason: "",
            orderNumber: "",
            message: "",
            attachment: null,
        },
        mode: "onBlur",
        reValidateMode: "onChange"
    });

    const [uploadKey, setUploadKey] = useState(0);

    const reason = watch("reason");
    const specificReason = watch("specificReason");

    const reasonOptions: SelectOption[] = useMemo(() => {
        return (issues ?? []).map((p) => ({value: p.externalId, label: p.title}));
    }, [issues]);

    const specificReasonOptions: SelectOption[] = useMemo(() => {
        const parent = (issues ?? []).find((p) => p.externalId === reason);
        return (parent?.children ?? []).map((c) => ({value: c.externalId, label: c.title}));
    }, [issues, reason]);

    const selectedChild: Issue | undefined = useMemo(() => {
        const parent = (issues ?? []).find((p) => p.externalId === reason);
        return (parent?.children ?? []).find((c) => c.externalId === specificReason);
    }, [issues, reason, specificReason]);

    const isOrderRelated = !!selectedChild?.rewardIssue;

    const currentOrderNumber = watch("orderNumber");

    const orderOptions: SelectOption[] = useMemo(() => {
        const redemptions = Array.isArray(redemptionsData) ? redemptionsData : [];

        const flattenedOrders = redemptions.flatMap((redemption) =>
            (redemption.orders ?? []).map((order) => {
                const orderDate = formatDate(order.date, {withTime: true});
                const orderNumber = order.friendlyId;

                return {
                    value: orderNumber,
                    label: [orderNumber, orderDate].filter(Boolean).join(" - "),
                    sortDate: order.date ?? redemption.dateTime ?? "",
                };
            })
        );

        const uniqueOrders = flattenedOrders.filter(
            (order, index, arr) =>
                arr.findIndex((x) => x.value === order.value) === index
        );

        uniqueOrders.sort((a, b) => {
            const da = new Date(a.sortDate).getTime();
            const db = new Date(b.sortDate).getTime();
            return db - da;
        });

        return uniqueOrders.map(({value, label}) => ({value, label}));
    }, [redemptionsData, i18n.language, t]);

    useEffect(() => {
        if (!isOrderRelated || !currentOrderNumber) return;

        const exists = orderOptions.some((opt) => opt.value === currentOrderNumber);
        if (!exists) {
            setValue("orderNumber", "", {shouldValidate: true});
        }
    }, [isOrderRelated, currentOrderNumber, orderOptions, setValue]);

    useEffect(() => {
        setValue("specificReason", "");
        clearErrors("specificReason");

        setValue("orderNumber", "");
        clearErrors("orderNumber");
    }, [reason, setValue, clearErrors]);

    useEffect(() => {
        if (!isOrderRelated) {
            setValue("orderNumber", "");
            clearErrors("orderNumber");
        }
    }, [isOrderRelated, setValue, clearErrors]);

    const formDisabled = isSubmitting || isLoadingIssues || isErrorIssues;

    const currentFile = watch("attachment");

    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        multiple: false,
        noClick: true,
        noKeyboard: true,
        disabled: formDisabled,
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "application/pdf": [".pdf"],
        },
        onDrop: async (acceptedFiles) => {
            const file = acceptedFiles?.[0] ?? null;
            setValue("attachment", file, {shouldValidate: true});
            await trigger("attachment");
        },
    });

    const removeFile = () => {
        setValue("attachment", null, {shouldValidate: true});
    };

    const submitFn: SubmitHandler<z.infer<typeof contactSchema>> = async (data) => {
        try {
            console.log("Contact submit", data);

            let uploadedFiles: string[] = [];

            if (data.attachment) {
                const uploadResults = await ContentIoService.upload(data.attachment, {
                    accessModifier: CONTENTIO_ACCESS_MODIFIER.PRIVATE,
                    destination: "zendesk",
                    moduleName: CONTENTIO_MODULE_ENUM.JNEXT,
                    middlePath: CONTENTIO_SUBMODULE_ENUM.UPLOADS,
                    subDir: "contact_form",
                });

                uploadedFiles = uploadResults
                    .filter((item) => item.uploaded && item.fileRef.trim().length > 0)
                    .map((item) => item.fileRef);

                if (uploadedFiles.length === 0) {
                    throw new Error("Attachment upload returned no valid fileRef");
                }
            }

            const payload: Ticket = {
                requester: {
                    name: data.name,
                    surname: data.surname,
                    email: data.email,
                },
                ticketBody: {
                    message: data.message,
                    reason: data.reason,
                    secondaryReason: data.specificReason || undefined,
                    orderId: data.orderNumber || undefined,
                    files: uploadedFiles,
                },
            };

            await ContactService.postTicket(payload);

            await openModal({
                tone: "success",
                title: t("contacts:form:feedback:success:title"),
                content: <p className="tp-xs">{t("contacts:form:feedback:success:message")}</p>,
                primaryAction: {
                    label: t("contacts:form:feedback:success:label")
                }
            });

            reset();
            setUploadKey(prev => prev + 1);

        } catch (e) {
            console.log("Error contact form", e);

            await openModal({
                tone: "error",
                title: t("contacts:form:feedback:error:title"),
                content: <p className="tp-xs">{t("contacts:form:feedback:error:message")}</p>,
                primaryAction: {
                    label: t("contacts:form:feedback:error:label")
                }
            });
        }
    };

    return (
        <form className="form contactForm" onSubmit={handleSubmit(submitFn)}>
            <div className="contactForm__grid contactForm__grid--2">
                <TextField
                    control={control}
                    name="name"
                    label={t("contacts:form:labels:name")}
                    type="text"
                    disabled={formDisabled}
                    error={errors.name?.message}
                    showValidatedIcon={false}
                />

                <TextField
                    control={control}
                    name="surname"
                    label={t("contacts:form:labels:surname")}
                    type="text"
                    disabled={formDisabled}
                    error={errors.surname?.message}
                    showValidatedIcon={false}
                />

                <TextField
                    control={control}
                    name="email"
                    label={t("contacts:form:labels:email")}
                    type="email"
                    disabled={formDisabled}
                    error={errors.email?.message}
                    showValidatedIcon={false}
                />

                <SelectField
                    control={control}
                    name="reason"
                    label={t("contacts:form:labels:reason")}
                    disabled={formDisabled}
                    error={errors.reason?.message}
                    options={reasonOptions}
                />

                <SelectField
                    control={control}
                    name="specificReason"
                    label={t("contacts:form:labels:secondary_reason")}
                    disabled={formDisabled || !reason}
                    error={errors.specificReason?.message}
                    options={specificReasonOptions}
                />

                <SelectField
                    control={control}
                    name="orderNumber"
                    label={`${t("contacts:form:labels:order_nr")}${isOrderRelated ? " *" : ""}`}
                    disabled={
                        formDisabled ||
                        !isOrderRelated ||
                        isLoadingRedemptions ||
                        isErrorRedemptions
                    }
                    error={errors.orderNumber?.message}
                    options={orderOptions}
                />

            </div>

            <TextareaField
                control={control}
                name="message"
                label={t("contacts:form:labels:message")}
                disabled={formDisabled}
                error={errors.message?.message}
                rows={6}
            />

            <div className="contactForm__upload">
                <div className="contactForm__uploadHint tp-s">
                    {t("contacts:form:labels:upload:p1")}{" "}
                    <button type="button" className="contactForm__uploadLink" onClick={open} disabled={formDisabled}>
                        {t("contacts:form:labels:upload:p2")}
                    </button>
                    {" "}
                    {t("contacts:form:labels:upload:p3")}
                </div>

                <div
                    key={uploadKey}
                    {...getRootProps({
                        className: [
                            "contactForm__dropzone",
                            isDragActive ? "contactForm__dropzone--active" : "",
                            formDisabled ? "contactForm__dropzone--disabled" : "",
                        ].join(" "),
                    })}
                >
                    <input {...getInputProps()} />

                    {!currentFile ? (
                        <div className="contactForm__dropzoneInner">
                            <span>{t("contacts:form:labels:upload_dropzone:p1")}{" "}</span>
                            <button type="button" className="contactForm__uploadLink" onClick={open}
                                    disabled={formDisabled}>
                                {t("contacts:form:labels:upload_dropzone:p2")}
                            </button>
                            <span>{" "}{t("contacts:form:labels:upload_dropzone:p3")}</span>
                        </div>
                    ) : (
                        <div className="contactForm__fileRow">
                            <div className="contactForm__fileInfo">
                                <div className="contactForm__fileName">{currentFile.name}</div>
                                <div className="contactForm__fileMeta">
                                    {(currentFile.size / 1024).toFixed(0)} KB
                                </div>
                            </div>

                            <button type="button" className="contactForm__fileRemove" onClick={removeFile} disabled={formDisabled}>
                                {t("contacts:form:labels:upload_dropzone:remove")}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="contactForm__footer">
                <div className="contactForm__required tp-xs">{t("contacts:form:validation_hint")}</div>

                <button type="submit" className="cta cta--medium" disabled={formDisabled || !isValid}>
                    <span className="label">{t("contacts:form:cta:label")}</span>
                    <span className="icon"><ChevronRightIcon/> </span>
                </button>
            </div>
        </form>
    );
}
